/* ============================================================
   Reformly Board PDP — hero behaviour.

   Scope is deliberately small. The export shipped ~58 KB of JS that
   built the whole page from data arrays; here the markup is already
   server-rendered, so this only handles what genuinely needs a click:
   gallery paging, colour choice, add-to-cart.

   No framework, no dependency, delegated listeners, one instance per
   section so multiple heroes on a page can't fight over state.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.rbpReady === '1') return;
    root.dataset.rbpReady = '1';

    var sectionId = root.dataset.sectionId;
    var colourIdx = parseInt(root.dataset.colourIndex, 10);

    /* ---------------- Gallery ----------------
       Swiper, not a hand-rolled transform. The previous version moved the
       track with translateX on click only — it bound no touch, pointer or
       drag handler at all, so on a phone a swipe did nothing to the
       gallery and the browser panned the page instead.

       Swiper owns the movement; this code only mirrors its index onto the
       thumbs, the caption and aria. If the bundle somehow fails to load,
       the thumb buttons still page the track by transform so the gallery
       is never completely dead. */
    var main = root.querySelector('.rbp-gal__main');
    var track = root.querySelector('[data-rbp-gal-track]');
    var thumbRow = root.querySelector('.rbp-gal__thumbs');
    var cap = root.querySelector('[data-rbp-gal-cap]');
    var next = root.querySelector('[data-rbp-gal-next]');
    var gal = null;
    var index = 0;

    // Re-queried rather than captured: the swatch handler replaces both
    // lists wholesale, so a NodeList taken at init would go stale.
    /* ALL slide elements, Swiper's loop duplicates included. Correct for
       pausing — every copy of a video has to be stopped — but NOT for
       indexing: with loop on, this list is longer than the gallery and
       slideEls()[i] can land on a clone. Use realCount()/activeSlideEl()
       for anything index-based. */
    function slideEls() { return root.querySelectorAll('[data-rbp-slide]'); }

    /* How many distinct slides there really are. Swiper 11 has no
       duplicate CLASS (that was v6-v10); instead every slide carries
       data-swiper-slide-index holding its real index, duplicates repeating
       the value they copy. Counting distinct values gives the true length. */
    function realCount() {
      var all = slideEls();
      var seen = {};
      var n = 0;
      for (var i = 0; i < all.length; i++) {
        var k = all[i].getAttribute('data-swiper-slide-index');
        if (k === null) k = String(i);
        if (!seen[k]) { seen[k] = 1; n++; }
      }
      return n;
    }

    /* The element actually on screen, which under loop may be a duplicate.
       Asking Swiper beats indexing the DOM ourselves. */
    function activeSlideEl() {
      if (gal && gal.slides && gal.slides[gal.activeIndex]) return gal.slides[gal.activeIndex];
      return slideEls()[index];
    }
    function thumbEls() { return root.querySelectorAll('[data-rbp-thumb]'); }

    /* Per-variant media, the same custom.media_gallery source OT006 uses
       (reformly-ot001-gallery). Liquid emits variant id -> media, so
       picking a colour swaps the gallery without a round trip. */
    var galData = null;
    var galDataEl = root.querySelector('[data-rbp-gal-data]');
    if (galDataEl) {
      try {
        galData = JSON.parse(galDataEl.textContent);
      } catch (e) {
        console.warn('[rbp] bad gallery data', e);
      }
    }

    function paint(i) {
      var moved = index !== i;
      index = i;

      /* One event for every gallery change — thumb, arrow, swipe or a
         programmatic move. Analytics listens for this rather than binding
         Swiper itself, so nothing outside this file needs to know the gallery
         is a Swiper. */
      if (moved) {
        root.dispatchEvent(new CustomEvent('rbp:slide', {
          detail: { index: i },
          bubbles: true
        }));
      }
      var thumbs = thumbEls();
      var slides = slideEls();
      for (var t = 0; t < thumbs.length; t++) {
        var on = t === index;
        thumbs[t].setAttribute('aria-current', on ? 'true' : 'false');
        // The visible marker is the 3px bar in the cell, not the button.
        var cell = thumbs[t].parentNode;
        if (cell && cell.classList) cell.classList.toggle('is-current', on);
      }
      var act = activeSlideEl();
      if (cap) {
        var img = act && act.querySelector('img');
        if (img && img.alt) cap.textContent = img.alt;
      }

      centreThumb(index);

      /* Only the slide on screen plays. Every other video is paused and
         rewound, so scrolling the gallery never leaves clips running in
         the background burning decode time. */
      // Videos are handled by syncVideos(), called on
      // slideChangeTransitionEnd so the decode work stays off the swipe.
      if (!gal) syncVideos();
    }

    /* Stop every copy first, then start only the element on screen.
       Comparing a DOM position against the real index was the bug: with loop
       on those are different numbering systems, so landing on the last slide
       started a video belonging to another one. */
    function syncVideos() {
      var slides = slideEls();
      for (var s = 0; s < slides.length; s++) {
        var vid = slides[s].querySelector('video');
        if (!vid || vid.paused) continue;
        vid.pause();
        vid.currentTime = 0;
      }
      var act = activeSlideEl();
      var actVid = act && act.querySelector('video');
      if (actVid) playActive(actVid);
    }

    /* Scrolling the gallery off screen pauses whatever is running in it, the
       same way the review cards behave (reformly-bp-swiper.js).

       Observed on `main`, not on the slides: the track moves the inactive
       slides sideways out of the viewport, so per-slide entries would fire on
       every horizontal page and pause a clip that is plainly on screen. The
       gallery box itself only ever leaves vertically.

       threshold 0 — fires when the box is fully gone. A fraction is wrong here
       because the gallery is taller than a phone viewport, so its ratio never
       reaches 0.5 and the crossing would never happen.

       Nothing resumes on the way back: syncVideos() starts a clip on a slide
       change, so leaving it paused is the resting state, and audio restarting
       under a scroll is exactly what this is meant to stop. */
    if (main && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) return;
          var vids = main.querySelectorAll('video');
          for (var v = 0; v < vids.length; v++) {
            if (!vids[v].paused) vids[v].pause();
          }
        },
        { threshold: 0 }
      ).observe(main);
    }

    /* Bring the active thumb into the middle of the strip.

       scrollIntoView() is avoided on purpose: with inline:'center' it also
       scrolls the nearest scrollable ANCESTOR, which on this page means the
       document jumps vertically when you pick a thumb. Setting scrollLeft
       on the strip itself moves only the strip.

       Clamped to the scrollable range so the first and last thumbs sit flush
       instead of leaving a gap the browser would snap back anyway. */
    function centreThumb(i) {
      if (!thumbRow) return;
      var cell = thumbRow.children[i];
      if (!cell) return;
      var max = thumbRow.scrollWidth - thumbRow.clientWidth;
      if (max <= 0) return;
      var target = cell.offsetLeft - (thumbRow.clientWidth - cell.offsetWidth) / 2;
      if (target < 0) target = 0;
      if (target > max) target = max;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (thumbRow.scrollTo) {
        thumbRow.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
      } else {
        thumbRow.scrollLeft = target;
      }
    }

    /* Sound: OFF, and this function deliberately does not touch it.

       The slide autoplays, so it inherits the `muted` the markup gives it
       (reformly-bp-hero.liquid renders these with muted: true) — which is also
       the only state browsers will autoplay at all.

       This used to unmute on the first pointerdown ANYWHERE on the page and
       stay unmuted for every clip after. A visitor tapping a swatch or opening
       the FAQ armed it without knowing, so by the time they scrolled to a video
       it started at full volume — the startle this is meant to avoid.

       Unmuting is now only ever the visitor's own doing, via the mute button on
       the control bar. That is per element, so the clip they unmute keeps its
       sound and the rest of the gallery stays silent. If they then slide away
       and back, an unmuted element may have its play() refused; the catch below
       mutes and retries rather than leaving a dead frame on screen. */
    function playActive(vid) {
      var p = vid.play();
      if (p && p.catch) {
        p.catch(function () {
          vid.muted = true;
          var again = vid.play();
          if (again && again.catch) again.catch(function () {});
        });
      }
    }

    function show(i) {
      var len = realCount();
      if (!len) return;
      var n = ((i % len) + len) % len;
      if (gal) {
        gal.slideToLoop(n);
        return; // slideChange repaints
      }
      if (track) track.style.transform = 'translateX(' + -n * 100 + '%)';
      paint(n);
    }

    if (main && slideEls().length && typeof window.Swiper === 'function') {
      gal = new window.Swiper(main, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 320,
        /* NO observer/observeParents here — deliberately.

           They were added while chasing slides that measured 566px inside a
           626px box. That turned out to be CSS, not timing: the header's
           unscoped `.swiper { padding: 0 3rem }` was winning after the shared
           stylesheets moved into the head, and it is fixed by specificity in
           reformly-bp-base.css. These flags never fixed it.

           They are actively harmful. observeParents attaches MutationObservers
           up the whole ancestor chain to <body>, and any mutation runs a full
           update() — a re-measure of every slide. The theme loads lazysizes,
           which stamps classes onto images as they scroll into view, so slow
           scrolling turned into a stream of re-measures across every Swiper on
           the page. That is the scroll lag. */
        /* Wrap past the last image instead of dead-ending. Loop clones the
           slides, so every index below has to be the REAL one: activeIndex
           counts clones, realIndex does not, and slideToLoop is the
           clone-aware slideTo. */
        loop: true,
        /* The rest of this block is reformly-luna.js:60 verbatim — that
           gallery was tuned for exactly this and reads noticeably smoother.

           loopAdditionalSlides: Swiper 11 shifts slides around the active one;
           reversing before a transition ends forces an instant re-translate,
           which is one janky frame. A spare slide either side means the
           reversal target is already in place.

           loopPreventsSliding defaults to true, so a reverse swipe mid-
           animation is swallowed and only applied once the first transition
           ends — that swallowed gesture is what reads as a dropped frame. */
        loopAdditionalSlides: 1,
        loopPreventsSliding: false,
        /* Fetch the neighbours before they are swiped to. Everything past
           slide 2 is loading="lazy", so without this the browser only starts
           the request once the slide is already on screen — which is the grey
           box then pop-in. Swiper 11 warms this many slides either side of
           the active one; 2 covers a fast double-swipe without pulling the
           whole gallery down on load. */
        lazyPreloadPrevNext: 2,
        // Vertical page scrolling must still work when the drag starts
        // mostly-vertically on the image. touchAngle/longSwipesRatio let a
        // vertical flick scroll the page while a short horizontal one still
        // commits.
        touchReleaseOnEdges: true,
        threshold: 4,
        touchAngle: 40,
        longSwipesRatio: 0.25,
        resistanceRatio: 0.6,
        a11y: { enabled: true },
        on: {
          slideChange: function () { paint(this.realIndex); },
          /* Video switching runs AFTER the transition, never during it.
             play()/pause() decode work stalls the compositor, so doing it
             inside slideChange janks the very swipe that triggered it —
             reformly-luna.js:161 makes the same split. */
          slideChangeTransitionEnd: function () { syncVideos(); }
        }
      });
    } else if (track) {
      track.style.transition = 'transform 320ms cubic-bezier(0.16,1,0.3,1)';
      track.style.willChange = 'transform';
    }

    if (next) next.addEventListener('click', function () { show(index + 1); });

    // Delegated: the thumbs are replaced on every colour change, so
    // per-element listeners would die with the old nodes.
    if (thumbRow) {
      thumbRow.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-rbp-thumb]');
        if (!b) return;
        show(parseInt(b.getAttribute('data-rbp-thumb'), 10) || 0);
      });
    }

    /* Swap the whole gallery for a colour. Rebuilds slides and thumbs from
       the variant's media, then tells Swiper the DOM moved under it —
       without update() it keeps the old slide count and translate. */
    function rebuildGallery(variantId) {
      if (!galData || !variantId || !galData[variantId] || !track) return;
      // .slice(1) mirrors the `offset: 1` in Liquid — the first entry is
      // the listing shot, not a gallery slide. Indices are renumbered from
      // 0 below so thumb -> slide mapping stays correct.
      var list = galData[variantId].slice(1);
      if (!list.length) return;

      var slideHtml = '';
      var thumbHtml = '';
      for (var i = 0; i < list.length; i++) {
        var m = list[i];
        var alt = String(m.alt || '').replace(/"/g, '&quot;');
        var body;
        if (m.type === 'video') {
          var srcs = '';
          for (var k = 0; k < (m.sources || []).length; k++) {
            srcs += '<source src="' + m.sources[k].url + '" type="' + m.sources[k].mime + '">';
          }
          body = '<video controls muted loop playsinline preload="metadata" poster="' + (m.poster || '') + '">' + srcs + '</video>';
        } else {
          body = '<img src="' + m.src + '" alt="' + alt + '" loading="' + (i === 0 ? 'eager' : 'lazy') + '" decoding="async">';
        }
        slideHtml += '<div class="rbp-gal__slide swiper-slide" data-rbp-slide="' + i + '">' + body + '</div>';

        if (i < 8) {
          thumbHtml +=
            '<div class="rbp-gal__thumbcell' + (i === 0 ? ' is-current' : '') + '">' +
            '<button type="button" role="tab" class="rbp-gal__thumb" data-rbp-thumb="' + i + '"' +
            ' aria-current="' + (i === 0 ? 'true' : 'false') + '" aria-label="Image ' + (i + 1) + '">' +
            '<img src="' + (m.thumb || m.src || m.poster) + '" alt="' + alt + '" loading="lazy" decoding="async">' +
            '</button><span class="rbp-gal__bar" aria-hidden="true"></span></div>';
        }
      }

      track.innerHTML = slideHtml;
      if (thumbRow) thumbRow.innerHTML = thumbHtml;

      if (gal) {
        /* The slides were just replaced, so the old clones are stale — rebuild
           them before updating, or the loop still wraps through the previous
           colour's images. */
        if (gal.loopDestroy) gal.loopDestroy();
        gal.update();
        if (gal.loopCreate) gal.loopCreate();
        gal.update();
        gal.slideToLoop(0, 0);
      } else if (track) {
        track.style.transform = 'translateX(0)';
      }
      paint(0);
    }

    /* ---------------- Colour ---------------- */
    var swatches = root.querySelectorAll('[data-rbp-colour]');
    var colourName = root.querySelector('[data-rbp-colour-name]');
    /* ---- cart snapshot, fetched one interaction early ----

       Adding to cart used to be THREE serial round trips before the drawer had
       anything to show: GET /cart.js (which gifts are already there), POST
       /cart/add.js, then the drawer's own GET /cart.js. The first one is pure
       latency in front of the POST — the buyer waits a full round trip before
       the request that matters is even sent.

       It cannot simply be dropped: the gift lines have to be deduped against
       the real cart, and a cached snapshot can be stale in the one direction
       that hurts — if a gift was removed in the drawer, a stale "already have
       it" silently costs the buyer a gift.

       So the request is kept, exactly as it was, and merely moved earlier.
       pointerdown and hover on any add-to-cart button start it; by the time the
       click resolves it is usually done or in flight, and the POST goes out
       immediately. Worst case (a keyboard activation with no prior hover) it
       behaves exactly as before.

       FRESH_MS exists because the cart can change between the warm and the
       click — the drawer, another tab, the gift sweep. Older than this and it
       is refetched rather than trusted. */
    var FRESH_MS = 8000;
    var cartWarm = null;
    var cartWarmAt = 0;

    function cartSnapshot() {
      var root_ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (cartWarm && (Date.now() - cartWarmAt) < FRESH_MS) return cartWarm;
      cartWarmAt = Date.now();
      cartWarm = fetch(root_ + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function (e) { cartWarm = null; throw e; });
      return cartWarm;
    }

    function warmCart(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-rbp-atc], [data-rbp-atc-proxy]')) cartSnapshot().catch(function () {});
    }

    ['pointerdown', 'touchstart'].forEach(function (evt) {
      document.addEventListener(evt, warmCart, { passive: true, capture: true });
    });
    document.addEventListener('pointerover', warmCart, { passive: true, capture: true });

    var atcs = root.querySelectorAll('[data-rbp-atc]');
    var colour = null;

    for (var c = 0; c < swatches.length; c++) {
      if (swatches[c].getAttribute('aria-checked') === 'true') {
        colour = swatches[c].dataset.rbpColour;
      }
      swatches[c].addEventListener('click', function () {
        for (var k = 0; k < swatches.length; k++) {
          swatches[k].setAttribute('aria-checked', 'false');
        }
        this.setAttribute('aria-checked', 'true');
        colour = this.dataset.rbpColour;
        if (colourName) colourName.textContent = colour;

        // Retarget add-to-cart at the variant for this colour. Liquid wrote
        // the id onto each swatch, so no variant lookup table is needed and
        // the URL is never rewritten (the theme's picker navigates; this
        // does not — same reason the offer pages suppress it).
        var vid = this.dataset.variant;
        // All of them — the sticky bar has its own button and would otherwise
        // keep adding the colour the page first rendered with.
        if (vid) {
          for (var b = 0; b < atcs.length; b++) atcs[b].dataset.variant = vid;
        }
        // Colour drives the gallery, same as OT006.
        if (vid) rebuildGallery(vid);

        /* ...and the sticky bar's thumbnail. The URL is on the swatch itself
           (data-thumb), written by Liquid as the variant's CART image, so this
           needs no lookup and matches what the drawer will show. Without it the
           bar kept the colour the page first rendered with. */
        var sthumb = document.querySelector('.rbp-sticky__thumb');
        if (sthumb && this.dataset.thumb) {
          /* srcset wins over src, so it has to go — otherwise the bar keeps
             showing the first colour's image at every DPR above 1. */
          sthumb.removeAttribute('srcset');
          sthumb.src = this.dataset.thumb;
        }

        root.dispatchEvent(new CustomEvent('rbp:colour', { detail: { colour: colour, variant: vid }, bubbles: true }));
      });
    }

    /* ---------------- Add to cart ---------------- */

    /* Every add-to-cart in the section, not just the first: the hero button
       and the sticky bar both carry data-rbp-atc. Binding only atcs[0] left
       the sticky bar inert AND holding whatever variant it rendered with. */
    for (var a = 0; a < atcs.length; a++) bindAtc(atcs[a]);

    /* The CTAs further down the page ("Join today", "Claim My Transformation")
       add to cart too. They live in their own sections, outside this hero's
       root, so they are collected from the document rather than from `root`.

       They carry no variant of their own on purpose — resolving it from the
       hero's button at click time means a colour swatch change is picked up
       automatically, instead of every section needing its own copy of the
       swatch wiring. */
    var proxies = document.querySelectorAll('[data-rbp-atc-proxy]');
    for (var q = 0; q < proxies.length; q++) bindAtc(proxies[q]);

    function bindAtc(btn) {
      // The hero button wraps its text; the sticky bar is a bare button.
      var labelEl = btn.querySelector('[data-rbp-atc-label]') || btn;
      var addingText = btn.dataset.adding || 'ADDING...';
      var addedText = btn.dataset.added || 'ADDED!';

      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-loading')) return;

        // Own variant if it has one (hero + sticky), otherwise the hero
        // button's current one — see the proxy note above.
        var variant = btn.dataset.variant || (atcs[0] && atcs[0].dataset.variant);
        if (!variant) {
          // Surface it rather than POSTing an empty id for an opaque 422.
          console.warn('[rbp] no variant id on add-to-cart');
          return;
        }

        /* Board + its three free gifts in ONE /cart/add.js.

           This section adds with a raw fetch, which liquid-ajax-cart does not
           observe, so reformly-free-gifts.js never sees a request-end and would
           not add the gifts until the next page load — the drawer would open
           showing a board and nothing else. Posting them together also means
           the cart is never briefly in a board-without-gifts state, which is
           the failure reformly-free-gifts.js:372 documents.

           Ids come from the cart engine (rypCart.giftIds) rather than being
           repeated here, so the list stays single-sourced. free-gifts.js still
           reconciles afterwards and finds nothing missing. _free_gift is what
           marks these as hidden lines for the drawer, the header count and the
           gift sweep. */
        var giftIds = (window.rypCart && window.rypCart.giftIds) || [];

        /* Gifts are per-cart, not per-board: three of them however many boards
           are bought. /cart/add.js ADDS to an existing line, so posting them
           unconditionally took a second click to qty 2 — and free-gifts.js,
           which normally pins them back to 1 (:814), never sees a raw fetch to
           correct it. So read the cart first and only send the ones missing.

           A GET before the POST rather than a corrective update after it: the
           cart is never briefly wrong, and the drawer opens on the real state.
           If the read fails, fall through to sending all of them — a duplicate
           gift is recoverable, a missing one is what the buyer notices. */
        /* ONE PROGRAMME IN THE CART, EVER.

           The 60-Day (this page) and the 30-Day (a boardless kit) are the same
           promise to a buyer, so holding both reads as a duplicate. The sweep
           in reformly-free-gifts.js already removes the loser, but only after
           the add has landed — someone who skips the drawer and goes straight
           to checkout in that window orders both. Resolving it here means the
           cart is never in that state at all.

           Priority comes from reformlyProgramGiftIds, which is ordered, so
           this file does not need to know that a board beats a kit. */
        var programIds = window.reformlyProgramGiftIds || [];

        function isProgram(id) { return programIds.indexOf(id) !== -1; }

        // The programme this page is about to grant, if any.
        var myProgram = null;
        for (var mp = 0; mp < giftIds.length && myProgram === null; mp++) {
          if (isProgram(giftIds[mp])) myProgram = giftIds[mp];
        }

        /* Highest-priority programme across what is already in the cart plus
           what this add would contribute. Returns null when neither. */
        function programWinner(have) {
          for (var p = 0; p < programIds.length; p++) {
            if ((have && have[programIds[p]]) || programIds[p] === myProgram) return programIds[p];
          }
          return null;
        }

        function buildItems(have) {
          var winner = programWinner(have);
          var list = [{ id: Number(variant), quantity: 1 }];
          for (var g = 0; g < giftIds.length; g++) {
            // A losing programme is never posted — on this page that cannot
            // happen today (the 60-Day outranks the kit's), but it keeps the
            // rule in one place rather than assuming the ordering.
            if (isProgram(giftIds[g]) && giftIds[g] !== winner) continue;
            if (!have || !have[giftIds[g]]) {
              list.push({ id: giftIds[g], quantity: 1, properties: { _free_gift: 'true' } });
            }
          }
          return list;
        }

        /* Programme lines already in the cart that the winner displaces.
           Cleared BEFORE the add rather than after, so the worst case is a
           cart briefly holding NO programme instead of two — a missing free
           gift the sweep restores, rather than a duplicate on the order. */
        function losingPrograms(have) {
          var winner = programWinner(have);
          var drop = [];
          for (var d = 0; d < programIds.length; d++) {
            if (programIds[d] !== winner && have && have[programIds[d]]) drop.push(programIds[d]);
          }
          return drop;
        }

        /* Same treatment the rest of the store uses while cart/add is in
           flight — text swapped to "ADDING...", disabled, dimmed — then
           "ADDED!" held for 2s on success and reset immediately on failure.
           Read off maison-sticky-cart.liquid:1471 and :1529, which is the
           behaviour OT002-OT006 all copy. */
        var originalText = labelEl.textContent;
        labelEl.textContent = addingText;
        btn.classList.add('is-loading');
        btn.setAttribute('aria-disabled', 'true');

        function reset() {
          labelEl.textContent = originalText;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-disabled');
        }

        var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        /* warmCart() has usually already run this by the time the click lands —
           see the note where it is defined. If it has, this resolves from the
           in-flight or completed promise and the POST below starts immediately
           instead of a full round trip later. The logic is identical either
           way; only the timing moves. */
        cartSnapshot()
          .then(function (cart) {
            var have = {};
            (cart.items || []).forEach(function (it) { have[it.variant_id || it.id] = true; });
            return have;
          })
          .catch(function () { return null; })
          /* Clear a displaced programme before adding, not after. Only fires
             when one is actually there, so the common path keeps its single
             request. A failure here is swallowed: the add still goes ahead and
             the sweep in reformly-free-gifts.js removes the loser as it always
             did, so the worst case is the behaviour we had before this. */
          .then(function (have) {
            var drop = losingPrograms(have);
            if (!drop.length) return have;
            var updates = {};
            drop.forEach(function (id) { updates[id] = 0; });
            return fetch(routeRoot + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates })
            })
              .then(function () {
                drop.forEach(function (id) { delete have[id]; });
                return have;
              })
              .catch(function () { return have; });
          })
          .then(function (have) {
            return fetch(routeRoot + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: buildItems(have) })
            });
          })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/add ' + r.status);
            return r.json();
          })
          .then(function () {
            /* Stamp the PostHog ids onto the cart. theme.liquid hooks this to
               liquid-ajax-cart's request-end, which never fires for a raw
               fetch — its own comment (:116) says a section adding this way
               must call rfStampCart() itself "or its sales go unattributed".
               Not awaited: this opens a drawer rather than redirecting, so
               there is no navigation to outrun, and the helper uses keepalive.
               Same shape as reformly-mini-reformer-hero.liquid:908. */
            if (window.rfStampCart) rfStampCart();

            root.dispatchEvent(new CustomEvent('rbp:added', { detail: { variant: variant }, bubbles: true }));

            // Before the drawer opens, not after — the Plus branch returns
            // early, and doing this last left the button reading "ADDING..."
            // for good on exactly the template this page uses.
            labelEl.textContent = addedText;
            setTimeout(reset, 2000);

            /* This template renders the Plus drawer, which is driven directly
               rather than by cart events — the same call every other Plus page
               makes after adding (reformly-mini-reformer-hero.liquid:911).
               The event path stays as the fallback for any template that gets
               this section while still on the maison drawer. */
            if (window.rypCart) {
              window.rypCart.open();
              return;
            }
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('liquid-ajax-cart:request-end', { bubbles: true }));
            document.body.classList.add('js-ajax-cart-open');
            document.documentElement.classList.add('js-ajax-cart-drawer-opened');
          })
          .catch(function (e) {
            /* Say so on the button. The drawer deliberately does not open on
               failure, so without this the only signal was the label snapping
               back to "ADD TO CART" — indistinguishable from a mis-click, and
               the buyer is left with an empty cart believing otherwise.
               One combined request means a bad gift id fails the board too. */
            console.error('[rbp] add to cart failed', e);
            labelEl.textContent = 'TRY AGAIN';
            setTimeout(reset, 2000);
          });
      });
    }

    // paint, not show: with Swiper already at index 0 slideTo(0) fires no
    // slideChange, so the thumbs would never get their initial state.
    paint(0);
    // ...and no slideChangeTransitionEnd either, so the first slide's video
    // needs starting explicitly now that paint() no longer touches video.
    syncVideos();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-rbp-hero]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target.querySelector('[data-rbp-hero]');
    if (r) init(r);
  });
})();

/* ------------------------------------------------------------
   Sticky add-to-cart. The export's rule is exactly:
     show = mainAtc.getBoundingClientRect().bottom < 0
   ------------------------------------------------------------ */
(function () {
  'use strict';
  function init() {
    var bar = document.querySelector('[data-rbp-sticky]');
    var main = document.querySelector('.rbp-atc');
    if (!bar || !main) return;
    bar.removeAttribute('hidden');
    var shown = false;

    function apply(show) {
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-visible', show);
    }

    /* An IntersectionObserver, NOT a scroll listener.

       The rule is still the export's — show the bar once the main add-to-cart
       has passed above the viewport — but how it is evaluated matters a lot.

       This used to call getBoundingClientRect() on every scroll event, with no
       throttling. That call forces a synchronous style-and-layout flush, and
       its cost scales with how much layout there is to flush. It was tolerable
       while the page was small; it stopped being tolerable when the FAQ answers
       went from one line each to twelve multi-paragraph ones, because every
       scroll event then had to reflow a far bigger document. Opening an answer
       made it worse again. That is the reported jank.

       IntersectionObserver does the same test off the main thread and only
       calls back when the answer actually CHANGES — no per-event work, no
       forced layout, nothing at all while scrolling through the middle of the
       page. `boundingClientRect` on the entry is supplied by the observer, so
       reading it here costs nothing either. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        apply(!e.isIntersecting && e.boundingClientRect.bottom <= 0);
      }, { threshold: 0 }).observe(main);
      return;
    }

    /* Fallback, still rAF-throttled so it can only force one layout per frame
       rather than one per event. */
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        apply(main.getBoundingClientRect().bottom < 0);
      });
    }, { passive: true });
    apply(main.getBoundingClientRect().bottom < 0);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   Gallery hover cursor — desktop only.

   Ported from reformly-005.js. Separate IIFE rather than folded into the
   section initialiser above: it needs nothing from that scope except the
   Swiper, and Swiper 11 hangs the instance off its own element (el.swiper),
   so this stays decoupled from how the gallery is built.
   ============================================================ */
(function () {
  function init() {
    document.querySelectorAll('[data-rbp-gal]').forEach(function (root) {
      var main = root.querySelector('.rbp-gal__main');
      var cursor = root.querySelector('[data-rbp-gal-cursor]');
      if (!main || !cursor) return;

      var svg = cursor.querySelector('svg');
      var desktop = function () { return window.innerWidth >= 900; };


      main.addEventListener('mouseenter', function () {
        if (desktop()) cursor.classList.add('is-visible');
      });

      main.addEventListener('mouseleave', function () {
        cursor.classList.remove('is-visible');
      });

      /* Only the control bar is off limits — not the whole player. Hiding the
         arrow over all of `.plyr` left video slides with no way to page at
         all, which is worse than the overlap it was fixing. */
      function onControls(e) {
        return !!(e.target.closest && e.target.closest('.plyr__controls, .plyr__control'));
      }

      main.addEventListener('mousemove', function (e) {
        if (!desktop()) return;
        if (onControls(e)) { cursor.classList.remove('is-visible'); return; }
        var rect = main.getBoundingClientRect();
        cursor.classList.add('is-visible');
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
        svg.style.transform = (e.clientX - rect.left) < rect.width / 2 ? 'scaleX(-1)' : '';
      });

      main.addEventListener('click', function (e) {
        if (!desktop()) return;
        var sw = main.swiper;
        if (!sw) return;
        // The bar owns its own clicks; everywhere else pages, video included.
        if (onControls(e)) return;
        var rect = main.getBoundingClientRect();
        if (e.clientX - rect.left >= rect.width / 2) sw.slideNext();
        else sw.slidePrev();
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   Trim the native control cluster on the gallery videos.

   iOS puts AirPlay/SharePlay routing and PiP alongside fullscreen; those two
   can be removed, fullscreen cannot — `controlsList="nofullscreen"` is
   Chromium-only and Safari ignores it, and the ::-webkit-media-controls-*
   pseudo-elements are not supported on iOS at all. So this clears everything
   that CAN be cleared and the fullscreen button stays.

   Applied here rather than on the tag because Shopify's `video_tag` filter
   does not pass arbitrary attributes through, and the swatch handler rebuilds
   the slides — hence the observer.
   ============================================================ */
(function () {
  function trim(scope) {
    var vids = (scope || document).querySelectorAll('[data-rbp-gal] video');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (v.dataset.rbpTrimmed) continue;
      v.setAttribute('x-webkit-airplay', 'deny');
      v.setAttribute('disablePictureInPicture', '');
      v.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback nofullscreen');

      /* Subtitles. There is no controlsList token for the CC button — it
         appears purely because the element HAS text tracks, so the tracks
         themselves have to go. Disabling them (track.mode = 'disabled') is not
         enough; the button stays and just does nothing. */
      var tracks = v.querySelectorAll('track');
      for (var t = tracks.length - 1; t >= 0; t--) tracks[t].remove();
      for (var k = 0; k < v.textTracks.length; k++) v.textTracks[k].mode = 'disabled';

      v.dataset.rbpTrimmed = '1';
    }
  }

  function init() {
    trim();
    document.querySelectorAll('[data-rbp-gal]').forEach(function (root) {
      // Colour swatches replace the slide list wholesale, so new <video>
      // elements appear without a page load.
      new MutationObserver(function () { trim(root); })
        .observe(root, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
   Swiping over a video.

   The native control layer calls stopPropagation on its touch events, so once
   it is on screen — which any press on play, sound, PiP or the scrubber leaves
   it — Swiper's listener on the container never sees the gesture and the
   gallery reads as frozen.

   Capture-phase listeners on an ancestor run BEFORE the target's own handlers
   and cannot be stopped by them, so the gesture is measured here and applied
   to Swiper directly.

   Only touches that start on a <video> are handled, and only if Swiper did not
   already move on its own — otherwise a normal swipe would advance twice.
   ============================================================ */
(function () {
  var MIN_X = 40;    // px of travel before it counts as a swipe
  var MAX_MS = 800;  // longer than this is a scrub, not a flick

  function init() {
    document.querySelectorAll('[data-rbp-gal]').forEach(function (root) {
      var main = root.querySelector('.rbp-gal__main');
      if (!main) return;

      var x0 = 0, y0 = 0, t0 = 0, onVideo = false, idx0 = null;

      main.addEventListener('touchstart', function (e) {
        var t = e.touches && e.touches[0];
        if (!t) return;
        onVideo = !!(e.target.closest && e.target.closest('video'));
        x0 = t.clientX; y0 = t.clientY; t0 = Date.now();
        idx0 = main.swiper ? main.swiper.realIndex : null;
      }, true);

      main.addEventListener('touchend', function (e) {
        if (!onVideo) return;
        onVideo = false;
        var sw = main.swiper;
        if (!sw) return;

        var t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        var dx = t.clientX - x0;
        var dy = t.clientY - y0;
        if (Date.now() - t0 > MAX_MS) return;
        if (Math.abs(dx) < MIN_X) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.2) return;  // vertical scroll

        /* Let Swiper have its chance first. If it did receive the gesture the
           index has already changed and this must not fire as well. */
        setTimeout(function () {
          if (idx0 !== null && sw.realIndex !== idx0) return;
          if (dx < 0) sw.slideNext(); else sw.slidePrev();
        }, 60);
      }, true);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
